/**
 * The brand's voice: one joke, told once per screen. To noodle is to dither; the product exists
 * so people make the thing instead. Each saying has one place, and a picture of Pip and friends
 * made for it (public/brand). Never in the product's working surfaces (node cards, menus,
 * settings): only where someone waits, arrives, or finds nothing.
 */
export type SayingScene =
    | 'start'    // the app starting, the sign-in being checked
    | 'open'     // a workspace opening
    | 'switch'   // leaving one workspace for another
    | 'home'     // going back to the library
    | 'cooking'  // a long generation
    | 'oops'     // an error, a page that is not there
    | 'welcome'  // the sign-in panel
    | 'joined'   // someone accepted an invitation
    | 'done'     // a generation or an export finished
    | 'sketch';  // the Sketch node with nothing drawn

export interface Saying {
    line: string;
    picture: string;
    alt: string;
}

export const SAYINGS: Record<SayingScene, Saying> = {
    start: { line: 'Noodles, Don’t Noodles.', picture: '/brand/welcome.webp', alt: 'Pip the fox slurping ramen at a harbour noodle stall at dusk' },
    open: { line: 'Fresh bowl, coming through.', picture: '/brand/loading-open.webp', alt: 'Pip opening a round door into a studio full of pinned pictures, a bowl of noodles in one paw' },
    switch: { line: 'Less noodling, more noodles.', picture: '/brand/loading-switch.webp', alt: 'Pip walking along a pier balancing a wobbling tower of noodle bowls' },
    home: { line: 'Noodles, Don’t Noodles.', picture: '/brand/loading-home.webp', alt: 'Pip walking up harbour steps at dusk towards a lit round window, carrying a takeaway box of noodles' },
    cooking: { line: 'Still cooking. Good noodles take a minute.', picture: '/brand/loading-cooking.webp', alt: 'An owl chef lifting fresh noodles out of a steaming pot at a harbour cart' },
    oops: { line: 'You noodled too hard.', picture: '/brand/oops.webp', alt: 'A kitten tangled in noodles on the pier, looking guilty' },
    welcome: { line: 'Noodles, Don’t Noodles.', picture: '/brand/welcome.webp', alt: 'Pip the fox slurping ramen at a harbour noodle stall at dusk' },
    joined: { line: 'A bigger table.', picture: '/brand/joined.webp', alt: 'Pip, an owl chef and a kitten sharing one long noodle across a table on the pier' },
    done: { line: 'Done. Al dente.', picture: '/brand/done.webp', alt: 'Pip slurping the last noodle with his eyes closed' },
    sketch: { line: 'Start from a line.', picture: '/brand/sketch-empty.webp', alt: 'A bear painting on a canvas with a noodle as a brush' },
};

/** The pictures of the scenes that show while something loads, fetched once the app is idle so they are there when a wait comes */
export const warmLoadingPictures = (): void => {
    if (typeof window === 'undefined') return;
    const scenes: SayingScene[] = ['open', 'switch', 'home'];
    const warm = () => { for (const scene of scenes) { const img = new Image(); img.decoding = 'async'; img.src = SAYINGS[scene].picture; } };
    if ('requestIdleCallback' in window) (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(warm);
    else setTimeout(warm, 2000);
};
